import { jsPDF } from 'jspdf';

export const generateAssessmentPDF = async (details, score, analysis, testType, answers, questions, config) => {
  const doc = new jsPDF();

  // Load logo image
  let logoData = null;
  try {
    const response = await fetch('/logo.png');
    const blob = await response.blob();
    logoData = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Could not load logo:', error);
  }

  // Get test name from questions data
  const testName = questions.testName || 'Assessment';
  const testCode = questions.testCode || testType;
  const currentDate = new Date();
  const dateStr = currentDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const timeStr = currentDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  // Professional Color Palette - Elegant neutral theme
  const colors = {
    primary: [51, 65, 85], // Slate-700
    secondary: [248, 250, 252], // Slate-50
    accent: [100, 116, 139], // Slate-500
    text: [31, 41, 55], // Gray-800
    textLight: [107, 114, 128], // Gray-500
    border: [226, 232, 240], // Slate-200
  };

  // Helper function to add header on each page
  const addHeader = () => {
    // Clean white background with subtle bottom border
    doc.setDrawColor(226, 232, 240); // Slate-200
    doc.setLineWidth(0.5);
    doc.line(15, 38, 195, 38);

    // Logo
    if (logoData) {
      // Add actual logo image
      try {
        doc.addImage(logoData, 'PNG', 15, 12, 16, 16);
      } catch (error) {
        // Fallback to text if image fails
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(51, 65, 85);
        doc.text("VNC", 23, 22, { align: 'center' });
      }
    } else {
      // Text-based logo
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(51, 65, 85);
      doc.text("VNC", 23, 22, { align: 'center' });
    }

    // Clinic Name - Elegant serif-style
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 65, 85); // Slate-700
    doc.text("Vohra Neuropsychiatry Centre", 35, 18);

    // Address - Subtle
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(107, 114, 128); // Gray-500
    doc.text("29/24, East Patel Nagar, New Delhi 110008", 35, 24);

    // Report Title - Right aligned, understated
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139); // Slate-500
    doc.text("Clinical Assessment Report", 195, 17, { align: 'right' });

    // Confidential badge - Softer red
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setFillColor(254, 242, 242); // Red-50
    doc.setDrawColor(252, 165, 165); // Red-300
    doc.setLineWidth(0.3);
    doc.roundedRect(159, 20, 36, 5.5, 1, 1, 'FD');
    doc.setTextColor(185, 28, 28); // Red-700
    doc.text("CONFIDENTIAL", 177, 24, { align: 'center' });
  };

  // Helper function to add footer
  const addFooter = (pageNum) => {
    doc.setFontSize(8);
    doc.setTextColor(...colors.textLight);
    doc.text(`Page ${pageNum}`, 210 / 2, 287, { align: 'center' });
    doc.text("© 2026 Vohra Neuropsychiatry Centre • All Rights Reserved", 210 / 2, 292, { align: 'center' });
  };

  // PAGE 1: Summary and Patient Details
  addHeader();
  let y = 52;

  // Combined Report & Patient Information Box
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, y, 180, 52, 2, 2, 'S');

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.textLight);

  // Row 1: Report ID, Date, Time
  doc.text("Report ID:", 20, y + 8);
  doc.text("Date:", 85, y + 8);
  doc.text("Time:", 145, y + 8);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.text);
  const reportId = Math.random().toString(36).substring(2, 11).toUpperCase();
  doc.text(reportId, 20, y + 14);
  doc.text(dateStr, 85, y + 14);
  doc.text(timeStr, 145, y + 14);

  // Row 2: Assessment, Supervising Clinician
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.textLight);
  doc.text("Assessment:", 20, y + 22);
  doc.text("Supervising Clinician:", 110, y + 22);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.text);
  doc.text(testName, 20, y + 28);
  doc.text(details.doctor, 110, y + 28);

  // Row 3: Patient Name, Date of Birth
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.textLight);
  doc.text("Patient Name:", 20, y + 38);
  doc.text("Date of Birth:", 110, y + 38);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.text);
  doc.text(details.patient, 20, y + 44);

  // Format DOB. Parse as YYYY-MM-DD without timezone shift so an ISO date
  // like "1990-01-01" does not roll back to the previous day in UTC-negative
  // timezones when passed through `new Date()`.
  const formatDob = (raw) => {
    if (!raw) return 'Not Provided';
    const iso = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    if (iso) {
      const year = parseInt(iso[1], 10);
      const month = parseInt(iso[2], 10);
      const day = parseInt(iso[3], 10);
      if (month < 1 || month > 12 || day < 1 || day > 31) return String(raw);
      return `${MONTHS[month - 1]} ${day}, ${year}`;
    }
    return String(raw);
  };
  const dobFormatted = formatDob(details.dob);
  doc.text(dobFormatted, 110, y + 44);

  // Horizontal separator lines
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.3);
  doc.line(20, y + 18, 190, y + 18);
  doc.line(20, y + 33, 190, y + 33);

  y += 62;

  // Assessment Results Section
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.primary);
  doc.text("ASSESSMENT RESULTS", 15, y);
  y += 3;

  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(1);
  doc.line(15, y, 68, y);
  y += 10;

  // Score Box
  doc.setFillColor(...colors.secondary); // Slate-50
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(1);
  doc.roundedRect(15, y, 180, 38, 3, 3, 'FD');

  // Determine how to display the score.
  // - Number (BDI, BAI, YBOCS, ...): show total + max
  // - Object with .total (ISAA, PANSS, CAPS5, CAARS, PCPTSD5, ASRS, IGDS9SF): show total + max
  // - Object with .isPositive (MDQ): show POSITIVE/NEGATIVE
  // - Other object (PID5, ASSIST): show the computed severity label only
  const isNumericScore = typeof score === 'number';
  const hasNumericTotal = !isNumericScore && score && typeof score === 'object' && typeof score.total === 'number';
  const hasBinaryFlag = !isNumericScore && score && typeof score === 'object' && typeof score.isPositive === 'boolean';
  const displayScore = isNumericScore ? score : hasNumericTotal ? score.total : 'N/A';
  const maxScore = config.maxScore || 0;
  const showNumericBlock = isNumericScore || hasNumericTotal;

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.textLight);
  doc.text(showNumericBlock ? "Total Score:" : "Screening Result:", 25, y + 14);

  if (showNumericBlock) {
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text(`${displayScore}`, 25, y + 28);

    doc.setFontSize(14);
    doc.setTextColor(...colors.textLight);
    doc.text(`/ ${maxScore}`, 45, y + 28);
  } else if (hasBinaryFlag) {
    // MDQ / similar: POSITIVE/NEGATIVE
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const resultColor = score.isPositive ? [239, 68, 68] : [34, 197, 94];
    doc.setTextColor(...resultColor);
    doc.text(score.isPositive ? "POSITIVE" : "NEGATIVE", 25, y + 28);
  } else {
    // Non-numeric, non-binary (PID5, ASSIST): show dash
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("SEE BELOW", 25, y + 28);
  }

  // Severity Level
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.textLight);
  doc.text(analysis.isBinary ? "Result:" : "Severity Level:", 115, y + 14);

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  // Color code based on severity or result
  let severityColor;
  const levelLower = analysis.level.toLowerCase();

  if (analysis.isBinary) {
    // MDQ binary results
    severityColor = levelLower.includes('positive') ? [239, 68, 68] : [34, 197, 94];
  } else if (levelLower.includes('subclinical') || levelLower.includes('normal') || levelLower.includes('low') || levelLower.includes('unlikely')) {
    severityColor = [34, 197, 94]; // Green
  } else if (levelLower.includes('mild')) {
    severityColor = [234, 179, 8]; // Yellow
  } else if (levelLower.includes('moderate') || levelLower.includes('borderline')) {
    severityColor = [249, 115, 22]; // Orange
  } else if (levelLower.includes('severe') || levelLower.includes('high') || levelLower.includes('extreme')) {
    severityColor = [239, 68, 68]; // Red
  } else {
    severityColor = [107, 114, 128]; // Gray (default)
  }
  doc.setTextColor(...severityColor);

  // Wrap long text if needed
  const levelText = analysis.level.toUpperCase();
  if (levelText.length > 25) {
    const levelLines = doc.splitTextToSize(levelText, 75);
    doc.setFontSize(12);
    doc.text(levelLines, 115, y + 24);
  } else {
    doc.text(levelText, 115, y + 28);
  }

  y += 48;

  // Clinical Interpretation
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.primary);
  doc.text("CLINICAL INTERPRETATION", 15, y);
  y += 3;

  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(1);
  doc.line(15, y, 82, y);
  y += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.text);
  const descLines = doc.splitTextToSize(analysis.desc, 180);
  doc.text(descLines, 15, y);
  y += descLines.length * 5 + 5;

  // Structured breakdown for object scores (domains / subscales / clusters / facets / per-substance)
  const renderBreakdownEntries = (title, entries) => {
    if (!entries || entries.length === 0) return;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text(title, 15, y);
    y += 3;
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(1);
    doc.line(15, y, 15 + Math.min(80, title.length * 2), y);
    y += 8;

    entries.forEach(([label, value]) => {
      if (y > 270) { addFooter(1); doc.addPage(); addHeader(); y = 52; }
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.text);
      const labelLines = doc.splitTextToSize(String(label), 140);
      doc.text(labelLines, 20, y + 5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...colors.primary);
      doc.text(String(value), 190, y + 5, { align: 'right' });
      y += Math.max(8, labelLines.length * 4 + 2);
    });
    y += 4;
  };

  if (!isNumericScore && score && typeof score === 'object') {
    if (score.domains) {
      renderBreakdownEntries("DOMAIN SCORES", Object.entries(score.domains));
    }
    if (score.rawScores) {
      renderBreakdownEntries("SUBSCALE SCORES", Object.entries(score.rawScores));
    }
    if (score.clusterB !== undefined) {
      renderBreakdownEntries("CLUSTER SCORES", [
        ['Cluster B (Intrusion)', score.clusterB],
        ['Cluster C (Avoidance)', score.clusterC],
        ['Cluster D (Cognition/Mood)', score.clusterD],
        ['Cluster E (Arousal)', score.clusterE],
        ['Dissociation items', score.dissociation]
      ]);
    }
    if (score.positive !== undefined && score.negative !== undefined && score.general !== undefined) {
      renderBreakdownEntries("PANSS SUBSCALES", [
        ['Positive (P1-P7)', score.positive],
        ['Negative (N1-N7)', score.negative],
        ['General Psychopathology (G1-G16)', score.general],
        ['Composite (Positive - Negative)', score.composite]
      ]);
    }
    if (score.domainScores) {
      renderBreakdownEntries("PID-5 DOMAIN AVERAGES", Object.entries(score.domainScores));
    }
    if (score.facetScores) {
      const facetEntries = Object.entries(score.facetScores).map(
        ([name, f]) => [name, `${f.raw} (avg ${f.average})`]
      );
      renderBreakdownEntries("PID-5 FACET SCORES", facetEntries);
    }
    if (score.substances && Object.keys(score.substances).length > 0) {
      const subEntries = Object.entries(score.substances).map(
        ([name, s]) => [name, `${s.score} (${s.risk} risk)`]
      );
      renderBreakdownEntries("ASSIST SUBSTANCE RISK", subEntries);
    }
  }

  // Severity Ranges Table (numeric-total scores use config.ranges).
  // Skip for binary tests and for any range that lacks numeric min/max, since
  // rendering `${range.min}-${range.max}` for those emits "undefined-undefined".
  const rangesAreNumeric = config.ranges &&
    config.ranges.length > 0 &&
    config.ranges.every(r => typeof r.min === 'number' && typeof r.max === 'number');
  if (showNumericBlock && !config.isBinaryTest && rangesAreNumeric) {
    const rangeScore = isNumericScore ? score : score.total;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("SEVERITY RANGES", 15, y);
    y += 3;

    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(1);
    doc.line(15, y, 56, y);
    y += 8;

    // Table for ranges
    const ranges = config.ranges;
    const rowHeight = 11;

    ranges.forEach((range) => {
      // Check if current score falls in this range
      const isCurrentRange = rangeScore >= range.min && rangeScore <= range.max;

      // Highlight current range
      if (isCurrentRange) {
        doc.setFillColor(255, 251, 235); // Yellow-50
        doc.roundedRect(15, y - 2, 180, rowHeight, 1, 1, 'F');
      }

      // Range label
      doc.setFontSize(10);
      doc.setFont("helvetica", isCurrentRange ? "bold" : "normal");
      doc.setTextColor(...colors.text);
      doc.text(range.label, 20, y + 6);

      // Score range
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.textLight);
      doc.text(`${range.min}-${range.max}`, 145, y + 6);

      // Indicator if current
      if (isCurrentRange) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...severityColor);
        doc.text("← CURRENT", 170, y + 6);
      }

      y += rowHeight;
    });
  } else if (analysis.isBinary && score.criteriaDetails) {
    // For MDQ, show screening criteria met/not met
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("SCREENING CRITERIA", 15, y);
    y += 3;

    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(1);
    doc.line(15, y, 58, y);
    y += 8;

    const criteria = [
      { label: `Question 1: ${score.q1Count || 0} of 13 symptoms (≥7 required)`, met: score.criteriaDetails.q1Met },
      { label: `Question 2: Co-occurrence during same period`, met: score.criteriaDetails.q2Met },
      { label: `Question 3: Moderate or Serious problem level`, met: score.criteriaDetails.q3Met }
    ];

    criteria.forEach((criterion) => {
      const statusColor = criterion.met ? [34, 197, 94] : [239, 68, 68]; // Green or Red
      const statusText = criterion.met ? "✓ MET" : "✗ NOT MET";

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.text);
      doc.text(criterion.label, 20, y + 6);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...statusColor);
      doc.text(statusText, 175, y + 6, { align: 'right' });

      y += 11;
    });
  }

  addFooter(1);

  // PAGE 2: Detailed Question Responses
  doc.addPage();
  addHeader();
  y = 52;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.primary);
  doc.text("DETAILED QUESTION RESPONSES", 15, y);
  y += 3;

  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(1);
  doc.line(15, y, 95, y);
  y += 10;

  doc.setFontSize(9);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...colors.textLight);
  doc.text(`${testName} - Complete Response Record`, 15, y);
  y += 12;

  // Questions and Answers
  const questionsData = questions.questions;
  let pageNum = 2;

  for (let i = 0; i < questionsData.length; i++) {
    const question = questionsData[i];
    const selectedAnswer = answers[i];
    const questionType = question.questionType || 'multiple_choice';

    // Skip informational-only questions
    if (question.informationalOnly) continue;

    // Get question text
    let questionText = '';
    if (questionType === 'yes_no_multi') {
      questionText = question.text || `Question ${i + 1}`;
    } else if (testType === 'BAI' && question.symptom) {
      questionText = question.symptom;
    } else {
      questionText = question.text || `Question ${i + 1}`;
    }

    // Determine all options and selected one
    let allOptions = [];
    let pointsEarned = 0;

    if (questionType === 'yes_no_multi') {
      // MDQ Q1: Special case - show summary
      const yesCount = Array.isArray(selectedAnswer) ? selectedAnswer.filter(a => a === 1).length : 0;
      allOptions = [{ text: `${yesCount} of ${question.subQuestions?.length || 0} symptoms reported`, isSelected: true }];
      pointsEarned = yesCount;
    } else if (questionType === 'yes_no') {
      // Simple yes/no - show both options
      allOptions = [
        { text: 'No', score: 0, isSelected: selectedAnswer === 0 },
        { text: 'Yes', score: 1, isSelected: selectedAnswer === 1 }
      ];
      pointsEarned = selectedAnswer;
    } else if (questionType === 'checkbox') {
      // BSDS checkbox
      allOptions = [
        { text: '☐ Does not describe me', score: 0, isSelected: selectedAnswer === 0 },
        { text: '✓ Describes me', score: question.points || 1, isSelected: selectedAnswer === 1 }
      ];
      pointsEarned = selectedAnswer === 1 ? (question.points || 1) : 0;
    } else if (questionType === 'multiple_choice_weighted') {
      // BSDS Q20 with weighted options
      allOptions = question.options?.map((opt, idx) => ({
        text: opt.text,
        score: opt.points,
        isSelected: idx === selectedAnswer
      })) || [];
      pointsEarned = question.options?.[selectedAnswer]?.points || 0;
    } else if (questionType === 'scale' || questionType === 'multiple_choice') {
      // Standard multiple choice - show all options
      allOptions = question.options?.map((opt, idx) => ({
        text: opt,
        score: idx,
        isSelected: idx === selectedAnswer
      })) || [];
      pointsEarned = selectedAnswer;
    } else if (questionType === 'likert' || questionType === 'clinician_rating') {
      // Likert / clinician rating: options store {label, points};
      // selectedAnswer is the point value (not the index) per TestStep.handleLikertClick.
      allOptions = question.options?.map((opt) => ({
        text: opt.label,
        score: opt.points,
        isSelected: opt.points === selectedAnswer
      })) || [];
      pointsEarned = selectedAnswer;
    } else if (questionType === 'gate_yes_no') {
      allOptions = [
        { text: 'No', score: 0, isSelected: selectedAnswer === 0 },
        { text: 'Yes', score: 1, isSelected: selectedAnswer === 1 }
      ];
      pointsEarned = selectedAnswer;
    } else if (questionType === 'assist_substance_screen') {
      // ASSIST: selectedAnswer is a structured object {substancesUsed, responses}
      const used = selectedAnswer?.substancesUsed || {};
      const activeSubs = Object.keys(used).filter(k => used[k]);
      if (activeSubs.length === 0) {
        allOptions = [{ text: 'No substances reported', isSelected: true }];
      } else {
        allOptions = activeSubs.flatMap(sub => {
          const responses = selectedAnswer?.responses?.[sub] || {};
          const lines = Object.entries(responses).map(
            ([qkey, pts]) => `  ${qkey.toUpperCase()}: ${pts} pts`
          );
          return [
            { text: sub + ':', isSelected: true },
            ...lines.map(l => ({ text: l, isSelected: false }))
          ];
        });
      }
      pointsEarned = activeSubs.length;
    }

    // Calculate dynamic box height
    const questionLines = doc.splitTextToSize(questionText, 165);
    let estimatedHeight = 12 + questionLines.length * 4.5;

    // Add height for options (compact)
    allOptions.forEach(opt => {
      const optLines = doc.splitTextToSize(opt.text, 155);
      estimatedHeight += optLines.length * 3.5 + 1;
    });

    const boxHeight = Math.max(24, estimatedHeight + 3);

    // Check if we need a new page
    if (y + boxHeight > 260) {
      addFooter(pageNum);
      pageNum++;
      doc.addPage();
      addHeader();
      y = 52;
    }

    // Question and response box
    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.5);
    doc.roundedRect(15, y, 180, boxHeight, 2, 2, 'S');

    // Question number circle
    doc.setFillColor(...colors.primary);
    doc.circle(23, y + 11, 4, 'F');

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(`${i + 1}`, 23, y + 12.5, { align: 'center' });

    // Question text (bold)
    let currentY = y + 9;
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.text);
    doc.text(questionLines, 31, currentY);
    currentY += questionLines.length * 4.5 + 3;

    // All options (compact list)
    doc.setFontSize(7.5);
    allOptions.forEach((opt) => {
      const optText = opt.text;
      const optLines = doc.splitTextToSize(optText, 155);

      if (opt.isSelected) {
        // Highlight selected option
        doc.setFont("helvetica", "bold");
        doc.setTextColor(51, 65, 85); // Slate-700
        doc.text('▸', 31, currentY);
        doc.text(optLines, 36, currentY);
      } else {
        // Non-selected option (lighter)
        doc.setFont("helvetica", "normal");
        doc.setTextColor(156, 163, 175); // Gray-400
        doc.text(optLines, 36, currentY);
      }

      // Show score/points for this option
      if (typeof opt.score !== 'undefined') {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...colors.textLight);
        const scoreLabel = (questionType === 'multiple_choice_weighted' || questionType === 'checkbox') ? `${opt.score}pt` : `[${opt.score}]`;
        doc.text(scoreLabel, 188, currentY, { align: 'right' });
      }

      currentY += optLines.length * 3.5 + 1;
    });

    // Total points earned (top right)
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 65, 85); // Slate-700
    if (questionType === 'multiple_choice_weighted' || questionType === 'checkbox') {
      doc.text(`${pointsEarned}pt`, 188, y + 11, { align: 'right' });
    } else {
      doc.text(`Score: ${pointsEarned}`, 188, y + 11, { align: 'right' });
    }

    y += boxHeight + 2.5;
  }

  // Important Note and Disclaimer on last page
  y += 12;
  if (y > 220) {
    // Add footer to current questions page before starting new page
    addFooter(pageNum);
    pageNum++;
    doc.addPage();
    addHeader();
    y = 52;
  }

  // Combined Important Note & Confidentiality Notice Box
  doc.setFillColor(254, 243, 199); // Amber-100
  doc.setDrawColor(251, 191, 36); // Amber-400
  doc.setLineWidth(1);
  doc.roundedRect(15, y, 180, 55, 2, 2, 'FD');

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(146, 64, 14); // Amber-900
  doc.text("IMPORTANT NOTE FOR CLINICIAN:", 20, y + 10);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120, 53, 15); // Amber-900
  const noteText = "This assessment is based on patient self-reporting. Results should be interpreted by a qualified healthcare professional in conjunction with clinical judgment and other diagnostic information.";
  const noteLines = doc.splitTextToSize(noteText, 170);
  doc.text(noteLines, 20, y + 17);

  // Separator line
  doc.setDrawColor(251, 191, 36); // Amber-400
  doc.setLineWidth(0.3);
  doc.line(20, y + 30, 190, y + 30);

  // Confidentiality Notice within same box
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(146, 64, 14); // Amber-900
  doc.text("CONFIDENTIALITY NOTICE:", 20, y + 36);

  doc.setFont("helvetica", "normal");
  const disclaimer = "This document contains confidential patient health information protected under HIPAA regulations. Unauthorized disclosure or distribution is strictly prohibited. This report should be filed in the patient's official medical record and handled in accordance with applicable privacy laws.";
  const disclaimerLines = doc.splitTextToSize(disclaimer, 170);
  doc.text(disclaimerLines, 20, y + 42);

  // Add footer to final page
  addFooter(pageNum);

  // Save PDF
  const fileName = `${details.patient.replace(/\s+/g, '_')}_${testCode}_${currentDate.toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
